import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { toast } from "sonner";
import { api, uploadFile, errorMessage } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/shared/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Check, ChevronRight, ChevronLeft, Upload, ShieldCheck } from "lucide-react";

const DOC_FIELDS = [
    { key: "shop_business_license", label: "Shop / Business License", hint: "GST / Municipality / Panchayat license." },
    { key: "pan_or_aadhaar", label: "PAN or Aadhaar", hint: "Owner's PAN or Aadhaar (single doc)." },
    { key: "shop_photo_or_offer_proof", label: "Shop Photo / Online Offer Proof", hint: "For OFFLINE, a shop photograph. For ONLINE, a website / offer proof." },
    { key: "business_address_proof", label: "Business Address Proof", hint: "Utility bill, rental agreement, etc." },
];

export default function OnboardingShop() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const [step, setStep] = useState(1);
    const [categories, setCategories] = useState([]);
    const [districts, setDistricts] = useState([]);
    const [docs, setDocs] = useState({}); // key -> { id, url, filename, purpose }
    const {
        register,
        handleSubmit,
        control,
        watch,
        formState: { errors },
    } = useForm({
        defaultValues: {
            mobile_number: user?.mobile_number || "",
            business_name: user?.business_name || "",
            business_type: user?.business_type || "OFFLINE",
            business_category: user?.business_category || "",
            business_district: user?.business_district || "",
        },
    });

    useEffect(() => {
        Promise.all([api.get("/categories"), api.get("/districts")])
            .then(([c, d]) => {
                setCategories(c.data);
                setDistricts(d.data);
            })
            .catch(() => toast.error("Could not load category/district data"));
    }, []);

    const businessType = watch("business_type");
    const step1Data = watch();

    const step1Valid = useMemo(
        () =>
            step1Data.mobile_number?.length >= 6 &&
            !!step1Data.business_name &&
            !!step1Data.business_type &&
            !!step1Data.business_category &&
            !!step1Data.business_district,
        [step1Data],
    );
    const step2Valid = DOC_FIELDS.every((f) => docs[f.key]?.id);

    if (!user) return <Navigate to="/login" replace />;
    if (user.role !== "SHOP_OWNER") return <Navigate to="/dashboard" replace />;

    const onFileUpload = async (key, file) => {
        try {
            const up = await uploadFile(file, "verification_doc");
            setDocs((prev) => ({ ...prev, [key]: up }));
            toast.success(`${key.replace(/_/g, " ")} uploaded`);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    const submitAll = async () => {
        try {
            const payload = {
                mobile_number: step1Data.mobile_number.trim(),
                business_name: step1Data.business_name.trim(),
                business_category: step1Data.business_category,
                business_district: step1Data.business_district,
                business_type: step1Data.business_type,
                verification_docs: {
                    shop_business_license: docs.shop_business_license?.id,
                    pan_or_aadhaar: docs.pan_or_aadhaar?.id,
                    shop_photo_or_offer_proof: docs.shop_photo_or_offer_proof?.id,
                    business_address_proof: docs.business_address_proof?.id,
                },
            };
            await api.patch("/auth/complete-shop-profile", payload);
            await refresh();
            setStep(3);
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="min-h-screen bg-neutral-50">
            <header className="border-b border-neutral-200 bg-white">
                <div className="mx-auto flex max-w-4xl items-center justify-between px-4 py-4">
                    <Logo size={32} />
                    <Stepper step={step} />
                </div>
            </header>
            <main className="mx-auto max-w-4xl px-4 py-8">
                {step === 1 && (
                    <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
                        <h1 className="text-2xl font-bold">Tell us about your business</h1>
                        <p className="mt-1 text-sm text-neutral-500">
                            You'll then upload verification docs. Our team
                            reviews within 24 hours.
                        </p>
                        <form className="mt-6 grid gap-4 sm:grid-cols-2" onSubmit={(e) => e.preventDefault()} data-testid="onboarding-step1-form">
                            <div>
                                <Label>Mobile number (with +91)</Label>
                                <Input
                                    placeholder="+91 98765 43210"
                                    inputMode="tel"
                                    data-testid="onb-mobile"
                                    {...register("mobile_number", { required: true, minLength: 6 })}
                                />
                                {errors.mobile_number && (
                                    <p className="mt-1 text-xs text-rose-600">
                                        Enter a valid mobile number
                                    </p>
                                )}
                            </div>
                            <div>
                                <Label>Business name</Label>
                                <Input
                                    placeholder="e.g. Kochi Fresh Supermart"
                                    data-testid="onb-business-name"
                                    {...register("business_name", { required: true })}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <Label>Business type</Label>
                                <Controller
                                    control={control}
                                    name="business_type"
                                    render={({ field }) => (
                                        <RadioGroup
                                            value={field.value}
                                            onValueChange={field.onChange}
                                            className="mt-2 grid grid-cols-2 gap-3"
                                        >
                                            {["OFFLINE", "ONLINE"].map((bt) => (
                                                <label
                                                    key={bt}
                                                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-4 ${field.value === bt ? "border-orange-500 bg-orange-50" : "border-neutral-200"}`}
                                                    data-testid={`onb-business-type-${bt}`}
                                                >
                                                    <RadioGroupItem value={bt} id={`bt-${bt}`} />
                                                    <div>
                                                        <div className="font-semibold">
                                                            {bt === "OFFLINE" ? "Physical shop" : "Online business"}
                                                        </div>
                                                        <div className="text-xs text-neutral-500">
                                                            {bt === "OFFLINE"
                                                                ? "Store, restaurant, salon, etc."
                                                                : "Website, D2C brand, delivery-only."}
                                                        </div>
                                                    </div>
                                                </label>
                                            ))}
                                        </RadioGroup>
                                    )}
                                />
                            </div>
                            <div>
                                <Label>Category</Label>
                                <Controller
                                    control={control}
                                    name="business_category"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                                            <SelectTrigger data-testid="onb-category">
                                                <SelectValue placeholder="Choose category" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {categories.map((c) => (
                                                    <SelectItem key={c.slug} value={c.slug}>
                                                        {c.emoji} {c.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                            <div>
                                <Label>District</Label>
                                <Controller
                                    control={control}
                                    name="business_district"
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Select value={field.value || undefined} onValueChange={field.onChange}>
                                            <SelectTrigger data-testid="onb-district">
                                                <SelectValue placeholder="Choose district" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {districts.map((d) => (
                                                    <SelectItem key={d.slug} value={d.slug}>
                                                        {d.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    )}
                                />
                            </div>
                        </form>
                        <div className="mt-6 flex justify-end">
                            <Button
                                onClick={() => setStep(2)}
                                disabled={!step1Valid}
                                className="nest-gradient text-white"
                                data-testid="onb-next-1"
                            >
                                Next: Upload docs <ChevronRight className="ml-2" size={16} />
                            </Button>
                        </div>
                    </div>
                )}

                {step === 2 && (
                    <div className="rounded-2xl border border-neutral-200 bg-white p-6 sm:p-8">
                        <h1 className="text-2xl font-bold">Upload verification documents</h1>
                        <p className="mt-1 text-sm text-neutral-500">
                            All 4 documents are required. Images or PDF up to 8 MB.
                        </p>
                        <div className="mt-6 grid gap-4 sm:grid-cols-2">
                            {DOC_FIELDS.map((f) => {
                                const uploaded = docs[f.key];
                                return (
                                    <label
                                        key={f.key}
                                        className={`flex cursor-pointer flex-col rounded-xl border-2 border-dashed p-4 ${uploaded ? "border-emerald-500 bg-emerald-50" : "border-neutral-300 bg-neutral-50"}`}
                                        data-testid={`onb-doc-${f.key}`}
                                    >
                                        <div className="flex items-center gap-2 text-sm font-semibold">
                                            {uploaded ? (
                                                <Check className="text-emerald-600" size={16} />
                                            ) : (
                                                <Upload size={16} />
                                            )}
                                            {f.label}
                                        </div>
                                        <div className="mt-1 text-xs text-neutral-500">{f.hint}</div>
                                        {uploaded ? (
                                            <div className="mt-3 text-xs text-emerald-700">
                                                Uploaded ✔ ({uploaded.filename})
                                            </div>
                                        ) : (
                                            <div className="mt-3 text-xs text-neutral-400">
                                                Click to choose file…
                                            </div>
                                        )}
                                        <input
                                            type="file"
                                            className="hidden"
                                            accept="image/*,application/pdf"
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (file) onFileUpload(f.key, file);
                                            }}
                                        />
                                    </label>
                                );
                            })}
                        </div>
                        <div className="mt-6 flex flex-col-reverse justify-between gap-3 sm:flex-row">
                            <Button
                                variant="outline"
                                onClick={() => setStep(1)}
                                data-testid="onb-back-2"
                            >
                                <ChevronLeft className="mr-1" size={16} /> Back
                            </Button>
                            <Button
                                disabled={!step2Valid}
                                onClick={submitAll}
                                className="nest-gradient text-white"
                                data-testid="onb-submit"
                            >
                                Submit for review <ChevronRight className="ml-2" size={16} />
                            </Button>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="rounded-2xl border border-neutral-200 bg-white p-8 text-center">
                        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full nest-gradient text-white nest-shadow">
                            <ShieldCheck size={30} />
                        </div>
                        <h1 className="mt-6 text-2xl font-bold">Submitted for review</h1>
                        <p className="mt-2 text-sm text-neutral-500">
                            Our team reviews shop applications within{" "}
                            <b>24 hours</b>. You'll be notified once your
                            account is approved.
                        </p>
                        <div className="mx-auto mt-6 max-w-md rounded-xl bg-neutral-50 p-4 text-left text-sm">
                            <div className="mb-2 font-semibold">Uploaded:</div>
                            {DOC_FIELDS.map((f) => (
                                <div key={f.key} className="flex items-center gap-2 py-1">
                                    <Check className="text-emerald-600" size={14} />
                                    <span>{f.label}</span>
                                </div>
                            ))}
                        </div>
                        <Button
                            onClick={() => navigate("/pending", { replace: true })}
                            className="mt-6 nest-gradient text-white"
                            data-testid="onb-goto-pending"
                        >
                            Continue
                        </Button>
                    </div>
                )}
            </main>
        </div>
    );
}

const Stepper = ({ step }) => {
    const items = [
        { n: 1, label: "Business" },
        { n: 2, label: "Documents" },
        { n: 3, label: "Review" },
    ];
    return (
        <div className="hidden gap-2 sm:flex">
            {items.map((it, i) => (
                <React.Fragment key={it.n}>
                    <div className="flex items-center gap-2">
                        <span
                            className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${step >= it.n ? "nest-gradient text-white" : "bg-neutral-200 text-neutral-500"}`}
                        >
                            {it.n}
                        </span>
                        <span className="text-sm font-medium text-neutral-700">
                            {it.label}
                        </span>
                    </div>
                    {i < items.length - 1 && (
                        <span className="mx-1 h-px w-6 self-center bg-neutral-300" />
                    )}
                </React.Fragment>
            ))}
        </div>
    );
};
